// v5 annual review evidence: the review questionnaire, which answers call for action, and the client's
// portfolio at the time. Kept free of command imports so services.ts can use it without a cycle.
import type { Review, WorkspaceView, Day } from './index.ts';
import { opt } from './cmd.ts';
import { isActivePolicy } from './rules.ts';
import { annualNow } from './policy-engine.ts';

export const REVIEW_QUESTIONS = ['life_changes', 'income_change', 'new_debts', 'health_changes', 'dependants_changed', 'goals_changed', 'beneficiaries_ok', 'cover_adequate', 'satisfied', 'referral_asked'] as const;
export type ReviewQuestion = typeof REVIEW_QUESTIONS[number];
export type ReviewAnswer = { a: 'yes' | 'no' | 'na'; note?: string | null };
/** answers that call for action: a change in the client's life, or a "no" to a confirmation question */
const ACTION_ON: Record<ReviewQuestion, 'yes' | 'no' | null> = { life_changes: 'yes', income_change: 'yes', new_debts: 'yes', health_changes: 'yes', dependants_changed: 'yes', goals_changed: 'yes', beneficiaries_ok: 'no', cover_adequate: 'no', satisfied: 'no', referral_asked: null };
export function reviewActions(answers: Partial<Record<ReviewQuestion, ReviewAnswer>> | null | undefined): ReviewQuestion[] {
  if (!answers) return [];
  return REVIEW_QUESTIONS.filter((q) => ACTION_ON[q] && answers[q]?.a === ACTION_ON[q]);
}
export function cleanReviewAnswers(answers: unknown): Partial<Record<ReviewQuestion, ReviewAnswer>> | null {
  if (!answers || typeof answers !== 'object') return null;
  const out: Partial<Record<ReviewQuestion, ReviewAnswer>> = {};
  for (const q of REVIEW_QUESTIONS) {
    const x = (answers as Record<string, ReviewAnswer | undefined>)[q];
    if (x && (x.a === 'yes' || x.a === 'no' || x.a === 'na')) out[q] = { a: x.a, note: opt(x.note ?? null) };
  }
  return Object.keys(out).length ? out : null;
}
/** the client's active annual premium at the time of the review (evidence) */
export function portfolioAnnualOf(v: WorkspaceView, personId: string, today: Day): number {
  return Math.round(v.where('policies', 'personId', personId).filter(isActivePolicy).reduce((s, p) => s + annualNow(v, p, today), 0) * 100) / 100;
}
export function reviewEvidence(r: Review): { answered: number; actions: ReviewQuestion[] } {
  const a = cleanReviewAnswers(r.answers) ?? {};
  return { answered: Object.keys(a).length, actions: reviewActions(a) };
}

